import { createListenerMiddleware, isAnyOf, type TypedStartListening } from '@reduxjs/toolkit';
import {
  setSegmentsCatalog,
  optimisticInsertSegment,
  optimisticUpdateSegment,
  optimisticRemoveSegment,
  rollbackToServerSnapshot,
} from '@/store/slices/segments/segmentsSlice';
import { selectAppliedSegmentCodes } from '@/store/slices/segments/selectors';
import { setPendingSelectionIfChanged } from '@/store/slices/pendingSelection/pendingSelectionSlice';
import type { CONFIG_SCHEMA } from '@/store/slices/screenConfig/screenConfigSlice';
import type { AppDispatch, RootState } from '@/store/store';

/**
 * Mirrors the `isApplied` set on the segments catalog into
 * `userSelection.segments` (string ULID codes).
 *
 * Listens to every action that can change which segments are applied
 * (catalog load, optimistic insert/update/remove, server-snapshot
 * rollback), recomputes the applied-code set, and dispatches
 * `setPendingSelectionIfChanged({ segments })` only when that set actually
 * differs from what's already committed in `userSelection` — the thunk
 * itself performs the deep-equality short-circuit, so this listener stays
 * simple and always safe to fire.
 *
 * Scoped to By-Segment (SEGMENT_ONLY) screens only: on By-Brand / By-Market
 * (SEGMENT_WITH_FILTERS, or no screen config yet), `userSelection.segments`
 * is the drawer-committed filter subset, not the raw server-applied set —
 * mirroring there would stomp the user's explicit selection.
 */
export const appliedSegmentsMirror = createListenerMiddleware();

type AppStartListening = TypedStartListening<RootState, AppDispatch>;

const startListening =
  appliedSegmentsMirror.startListening as AppStartListening;

startListening({
  matcher: isAnyOf(
    setSegmentsCatalog,
    optimisticInsertSegment,
    optimisticUpdateSegment,
    optimisticRemoveSegment,
    rollbackToServerSnapshot,
  ),
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const mode = (state.screenConfig as CONFIG_SCHEMA | undefined)
      ?.segmentFilterModalMode;
    if (mode !== 'SEGMENT_ONLY') return; // segments are a drawer-committed filter dimension on other screens
    const applied = selectAppliedSegmentCodes(state);
    listenerApi.dispatch(setPendingSelectionIfChanged({ segments: applied }));
  },
});
